import { Banknote, Building, FileText, Info, MapPin, Plus, Search, Users } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { searchCompanyByIdentifier, searchCompanyByName, BodaccRecord, CompanySearchResponse } from "interfaces/scanner";
import { toast } from "react-toastify";
import { ICompany, CompanyFormData } from "interfaces/company";
import { createAdminCompany } from "interfaces/admin/companies";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Input } from "../../components/ui/Input";

// Helper functions for search URLs
const generateGoogleSearchUrl = (firstName: string, lastName: string, companyName?: string) => {
    const query = companyName
        ? `${firstName.split(",")[0]} ${lastName} "${companyName}"`
        : `"${firstName.split(",")[0]} ${lastName}"`;
    return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
};

const generateLinkedInSearchUrl = (firstName: string, lastName: string, companyName?: string) => {
    const query = `${firstName.split(",")[0]} ${lastName}`;
    return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(query)}`;
};

// Google and LinkedIn SVG icons as components
const GoogleIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const LinkedInIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0077B5"/>
    </svg>
);

// Helper function for company Google search
const generateCompanyGoogleSearchUrl = (companyName: string) => {
    return `https://www.google.com/search?q=${encodeURIComponent(`"${companyName}"`)}`;
};

// Helper function for Societe.com search
const generateSocieteComSearchUrl = (denomination: string, siren: string) => {
    const slug = denomination
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Remove accents
        .replace(/[^a-z0-9]/g, "-") // Replace non-alphanumeric with dashes
        .replace(/-+/g, "-") // Replace multiple dashes with single dash
        .replace(/^-|-$/g, ""); // Remove leading/trailing dashes
    return `https://www.societe.com/societe/${slug}-${siren.replaceAll(' ', '')}.html`;
};

// Helper function for Pappers search
const generatePappersSearchUrl = (denomination: string, siren: string) => {
    const slug = denomination
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Remove accents
        .replace(/[^a-z0-9]/g, "-") // Replace non-alphanumeric with dashes
        .replace(/-+/g, "-") // Replace multiple dashes with single dash
        .replace(/^-|-$/g, ""); // Remove leading/trailing dashes
    return `https://www.pappers.fr/entreprise/${slug}-${siren.replaceAll(' ', '')}`;
};

// Helper function to open all searches for founders
const openAllSearches = (record: BodaccRecord) => {
    const companyName = record?.company.denomination || "";
    
    // Open company Google search first
    if (companyName) {
        const companyUrl = generateCompanyGoogleSearchUrl(companyName);
        window.open(companyUrl, '_blank', 'noopener,noreferrer');
    }
    
    // Get all founders/partners with names
    const allPeople: Array<{firstName: string, lastName: string, fullName: string}> = [];
    
    // Add founders
    if (record?.founders && record?.founders.length > 0) {
        record?.founders.forEach(founder => {
            const firstName = founder.prenom || founder.firstName || "";
            const lastName = founder.nom || founder.lastName || "";
            const fullName = founder.fullName || `${firstName} ${lastName}`.trim();
            
            if (firstName && lastName) {
                allPeople.push({ firstName, lastName, fullName });
            }
        });
    }
    
    // Add partners (only physical persons)
    if ((!record?.founders || record?.founders.length === 0) && record?.partners && record?.partners.length > 0) {
        record?.partners.forEach(partner => {
            if (partner.type !== 'moral' && partner.name) {
                const nameParts = partner.name.split(' ');
                const firstName = nameParts[0] || "";
                const lastName = nameParts.slice(1).join(' ') || "";
                
                if (firstName && lastName) {
                    allPeople.push({ firstName, lastName, fullName: partner.name });
                }
            }
        });
    }
    
    console.log(`Opening searches for ${allPeople.length} people:`, allPeople);
    
    // Open Google and LinkedIn searches for each person with proper delays
    allPeople.forEach((person, index) => {
        const googleUrl = generateGoogleSearchUrl(person.firstName, person.lastName, companyName);
        const linkedinUrl = generateLinkedInSearchUrl(person.firstName, person.lastName, companyName);
        
        // Google search for this person
         window.open(googleUrl, '_blank', 'noopener,noreferrer');
        
        // LinkedIn search for this person
         window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
    });
    
    // Show a toast notification
    const totalTabs = (companyName ? 1 : 0) + (allPeople.length * 2);
    if (totalTabs > 0) {
        setTimeout(() => {
            toast.success(`Ouverture de ${totalTabs} onglets de recherche...`);
        }, 100);
    } else {
        toast.warning("Aucune donnée disponible pour effectuer les recherches");
    }
};

const ScanCompany: React.FC = () => {
    const { query } = useParams<{ query?: string }>();
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState<"identifier" | "name">("identifier");
    const [loading, setLoading] = useState(false);
    const [searchResult, setSearchResult] = useState<CompanySearchResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
    const [selectedCompanyData, setSelectedCompanyData] = useState<BodaccRecord | null>(null);

    // Check if user is admin
    const isAdmin = localStorage.getItem("isAdmin") === "true";

    // Auto-search if query is provided in URL
    useEffect(() => {
        if (query) {
            const decoded = decodeURIComponent(query);
            // Accept SIREN (9 digits) or SIRET (14 digits)
            if (/^\d{9}$/.test(decoded) || /^\d{14}$/.test(decoded)) {
                setSearchType("identifier");
                setSearchQuery(decoded);
                performSearch(decoded, "identifier");
            } else {
                setSearchType("name");
                setSearchQuery(decoded);
                performSearch(decoded, "name");
            }
        }
    }, [query]);

    const performSearch = async (query: string, type: "identifier" | "name") => {
        if (!query.trim()) {
            toast.error("Veuillez saisir un terme de recherche");
            return;
        }

        if (type === "identifier") {
            const clean = query.replace(/\s/g, "");
            if (!/^\d{9}$/.test(clean) && !/^\d{14}$/.test(clean)) {
                toast.error("Saisissez un SIREN (9 chiffres) ou un SIRET (14 chiffres)");
                return;
            }
        } else {
            if (query.length < 3) {
                toast.error("Le nom de l'entreprise doit contenir au moins 3 caractères");
                return;
            }
        }

        setLoading(true);
        setError(null);
        setSearchResult(null);

        try {
            let response;

            if (type === "identifier") {
                const clean = query.replace(/\s/g, "");
                response = await searchCompanyByIdentifier(clean);
            } else {
                response = await searchCompanyByName(query, 20);
            }
            
            if (response.error) {
                setError(response.error.message || "Erreur lors de la recherche");
                toast.error(response.error.message || "Erreur lors de la recherche");
            } else if (response.data) {
                setSearchResult(response.data);
                const recordCount = response.data.meta.recordCount;
                if (recordCount === 0) {
                    toast.info("Aucun résultat trouvé");
                } else {
                    toast.success(`${recordCount} résultat(s) trouvé(s)`);
                }
            }
        } catch (err: any) {
            setError("Une erreur inattendue s'est produite");
            toast.error("Une erreur inattendue s'est produite");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        await performSearch(searchQuery, searchType);
    };

    const detectedIdentifierType = (value: string): "siret" | "siren" | null => {
        const clean = value.replace(/\s/g, "");
        if (/^\d{14}$/.test(clean)) return "siret";
        if (/^\d{9}$/.test(clean)) return "siren";
        return null;
    };

    const formatAddress = (address: any) => {
        if (!address) return "Non renseignée";
        
        const parts = [
            address.streetNumber,
            address.streetType,
            address.streetName,
            address.postalCode,
            address.city,
            address.country
        ].filter(Boolean);
        
        return parts.length > 0 ? parts.join(" ") : "Non renseignée";
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "Non renseignée";
        try {
            return new Date(dateString).toLocaleDateString("fr-FR");
        } catch {
            return dateString;
        }
    };

    const handleCreateCompany = (record: BodaccRecord) => {
        setSelectedCompanyData(record);
        setShowCreateCompanyModal(true);
    };

    // CreateCompanyModal component
    const CreateCompanyModal: React.FC<{
        isOpen: boolean;
        onClose: () => void;
        companyData: BodaccRecord;
    }> = ({ isOpen, onClose, companyData }) => {
        const [formData, setFormData] = useState<Partial<ICompany>>({});
        const [submitting, setSubmitting] = useState(false);

        useEffect(() => {
            if (isOpen && companyData) {
                // Pre-populate form with BODACC data
                let foundersData = companyData.founders || [];
                
                // If no founders, try to extract from partners (physical persons only)
                if (foundersData.length === 0 && companyData.partners) {
                    foundersData = companyData.partners
                        .filter(partner => partner.type !== 'moral' && partner.name)
                        .map(partner => ({
                            fullName: partner.name,
                            role: partner.role,
                            firstName: "",
                            lastName: "",
                            prenom: "",
                            nom: "",
                            qualite: partner.role
                        }));
                }

                const founders = foundersData.map(founder => {
                    // Extract first and last names from BODACC data
                    const firstName = founder.prenom || founder.firstName || "";
                    const lastName = founder.nom || founder.lastName || "";
                    const fullName = founder.fullName || `${firstName} ${lastName}`.trim();
                    
                    // If we have individual names, use them; otherwise try to split fullName
                    let finalFirstName = firstName;
                    let finalLastName = lastName;
                    
                    if (!firstName && !lastName && fullName) {
                        // Try to split the full name
                        const nameParts = fullName.split(' ');
                        finalFirstName = nameParts[0] || "";
                        finalLastName = nameParts.slice(1).join(' ') || "";
                    }

                    return {
                        firstName: finalFirstName,
                        lastName: finalLastName,
                        name: fullName,
                        title: founder.qualite || founder.role || ""
                    };
                }).filter(f => f.firstName || f.lastName || f.name);

                const contacts: ICompany['contacts'] = [];
                
                // Try to extract website from company data if available
                const website = "";

                setFormData({
                    name: companyData.company.denomination || "",
                    siren: companyData.company.registrationNumber || "",
                    imageUrl: "",
                    website,
                    founders,
                    contacts
                });
            }
        }, [isOpen, companyData]);

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setSubmitting(true);

            try {
                await createAdminCompany(formData as CompanyFormData);
                toast.success("Entreprise créée avec succès");
                onClose();
            } catch (error: any) {
                toast.error(error.response?.data?.message || "Erreur lors de la création de l'entreprise");
            } finally {
                setSubmitting(false);
            }
        };

        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-screen items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
                    <div className="relative bg-surface rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-ink">
                                Créer une entreprise
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-ink-faint hover:text-ink-muted"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-ink-muted mb-1">
                                    Nom de l'entreprise *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name || ""}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-ink"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink-muted mb-1">
                                    SIREN *
                                </label>
                                <input
                                    type="text"
                                    value={formData.siren || ""}
                                    onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
                                    className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-ink"
                                    maxLength={9}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink-muted mb-1">
                                    URL du site web
                                </label>
                                <input
                                    type="url"
                                    value={formData.website || ""}
                                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                    className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-ink"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink-muted mb-1">
                                    URL de l'image
                                </label>
                                <input
                                    type="url"
                                    value={formData.imageUrl || ""}
                                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                                    className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-ink"
                                />
                            </div>

                            {formData.founders && formData.founders.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-ink-muted mb-2">
                                        Fondateurs détectés
                                    </label>
                                    <div className="space-y-2 max-h-32 overflow-y-auto">
                                        {formData.founders.map((founder, index) => (
                                            <div key={index} className="p-2 bg-surface-sunken rounded text-sm">
                                                <div className="font-medium">
                                                    {founder.firstName && founder.lastName 
                                                        ? `${founder.firstName} ${founder.lastName}`
                                                        : founder.name || "Nom non renseigné"
                                                    }
                                                </div>
                                                {founder.title && <div className="text-ink-muted">{founder.title}</div>}
                                                {founder.firstName && founder.lastName && (
                                                    <div className="text-xs text-ink-faint mt-1">
                                                        Prénom: {founder.firstName} | Nom: {founder.lastName}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2 text-ink-muted bg-surface-sunken rounded-lg hover:bg-border transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {submitting ? "Création..." : "Créer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    };

    const CompanyCard: React.FC<{ record: BodaccRecord }> = ({ record }) => (
        <div className="bg-surface rounded-lg border border-border overflow-hidden hover:shadow-sm transition-shadow duration-200">
            {/* Company Header */}
            <div className="border-b border-border p-6">
                <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                        {record?.company.denomination?.charAt(0) || "E"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-1">
                                    <h2 className="text-2xl font-semibold text-ink truncate">
                                        {record?.company.denomination || "Dénomination non renseignée"}
                                    </h2>
                                    {record?.company.denomination && (
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const url = generateCompanyGoogleSearchUrl(record?.company.denomination);
                                                window.open(url, '_blank', 'noopener,noreferrer');
                                            }}
                                            className="p-2 rounded-full hover:bg-surface-sunken transition-colors duration-150 flex-shrink-0"
                                            title={`Rechercher ${record?.company.denomination} sur Google`}
                                        >
                                            <GoogleIcon className="w-5 h-5 text-ink-muted hover:text-ink" />
                                        </button>
                                    )}
                                    {/* Societe.com button */}
                                    {record?.company.registrationNumber && record?.company.denomination && (
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const url = generateSocieteComSearchUrl(record?.company.denomination, record?.company.registrationNumber);
                                                window.open(url, '_blank', 'noopener,noreferrer');
                                            }}
                                            className="px-3 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200 transition-colors duration-150 flex-shrink-0"
                                            title={`Voir sur Societe.com`}
                                        >
                                            Societe.com
                                        </button>
                                    )}
                                    {/* Pappers button */}
                                    {record?.company.registrationNumber && record?.company.denomination && (
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const url = generatePappersSearchUrl(record?.company.denomination, record?.company.registrationNumber);
                                                window.open(url, '_blank', 'noopener,noreferrer');
                                            }}
                                            className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors duration-150 flex-shrink-0"
                                            title={`Voir sur Pappers`}
                                        >
                                            Pappers
                                        </button>
                                    )}
                                </div>
                                {record?.company.commercialName && (
                                    <p className="text-ink-muted mb-2">
                                        {record?.company.commercialName}
                                    </p>
                                )}
                            </div>
                            <div className="ml-4 text-right flex gap-2">
                                {isAdmin && (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleCreateCompany(record);
                                        }}
                                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-150"
                                        title="Créer une entreprise à partir de ces données"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Créer Entreprise
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openAllSearches(record);
                                    }}
                                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-ink text-ink-inverse hover:bg-ink/90 focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-colors duration-150"
                                    title="Ouvrir toutes les recherches (Google entreprise + Google et LinkedIn pour tous les dirigeants)"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    Open All
                                </button>
                            </div>
                        </div>
                        
                        {/* Key Info Pills */}
                        <div className="flex flex-wrap gap-2 mt-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-surface-sunken text-ink">
                                SIREN: {record?.company.registrationNumber}
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-surface-sunken text-ink-muted">
                                {record?.company.legalForm || "Forme juridique non renseignée"}
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                                {record?.announcementType}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Company Details */}
            <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                        {/* Address */}
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-ink-muted" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-ink mb-1">Adresse du siège social</h3>
                                <p className="text-sm text-ink-muted leading-relaxed">
                                    {formatAddress(record?.company.address)}
                                </p>
                            </div>
                        </div>

                        {/* Capital */}
                        {record?.company.capital && (
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Banknote className="w-5 h-5 text-ink-muted" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-ink mb-1">Capital social</h3>
                                    <p className="text-sm text-ink-muted">
                                        {parseInt(record?.company.capital.amount).toLocaleString('fr-FR')} {record?.company.capital.currency || "EUR"}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Legal Acts */}
                        {record?.legalActs && (
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-ink-muted" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-ink mb-1">Informations légales</h3>
                                    <div className="space-y-1 text-sm text-ink-muted">
                                        {record?.legalActs.creationDate && (
                                            <p>Date de création: {formatDate(record?.legalActs.creationDate)}</p>
                                        )}
                                        {record?.legalActs.registrationDate && (
                                            <p>Immatriculation: {formatDate(record?.legalActs.registrationDate)}</p>
                                        )}
                                        <p>Publication BODACC: {formatDate(record?.publicationDate)}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                        {/* Founders */}
                        {record?.founders && record?.founders.length > 0 && (
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Users className="w-5 h-5 text-ink-muted" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-ink mb-2">
                                        Dirigeants ({record?.founders.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {record?.founders.slice(0, 3).map((founder, index) => {
                                            const fullName = founder.fullName || 
                                                `${founder.prenom || founder.firstName || ""} ${founder.nom || founder.lastName || ""}`.trim() ||
                                                "Nom non renseigné";
                                            const role = founder.qualite || founder.role || "Rôle non renseigné";
                                            
                                            // Extract first and last name for search URLs
                                            const firstName = founder.prenom || founder.firstName || "";
                                            const lastName = founder.nom || founder.lastName || "";
                                            const companyName = record?.company.denomination || "";
                                            
                                            const handleGoogleSearch = (e: React.MouseEvent) => {
                                                e.preventDefault();
                                                if (firstName && lastName) {
                                                    const url = generateGoogleSearchUrl(firstName, lastName, companyName);
                                                    window.open(url, '_blank', 'noopener,noreferrer');
                                                }
                                            };
                                            
                                            const handleLinkedInSearch = (e: React.MouseEvent) => {
                                                e.preventDefault();
                                                if (firstName && lastName) {
                                                    const url = generateLinkedInSearchUrl(firstName, lastName, companyName);
                                                    window.open(url, '_blank', 'noopener,noreferrer');
                                                }
                                            };
                                            
                                            return (
                                                <div key={index} className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-surface-sunken rounded-full flex items-center justify-center flex-shrink-0">
                                                        <span className="text-xs font-medium text-ink-muted">
                                                            {fullName.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-ink truncate">{fullName}</p>
                                                        <p className="text-xs text-ink-faint truncate">{role}</p>
                                                    </div>
                                                    {/* Search Icons */}
                                                    {firstName && lastName && (
                                                        <div className="flex items-center gap-1 ml-2">
                                                            <button
                                                                onClick={handleGoogleSearch}
                                                                className="p-1 rounded-full hover:bg-surface-sunken transition-colors duration-150"
                                                                title={`Rechercher ${fullName} sur Google`}
                                                            >
                                                                <GoogleIcon className="w-4 h-4 text-ink-muted hover:text-ink" />
                                                            </button>
                                                            <button
                                                                onClick={handleLinkedInSearch}
                                                                className="p-1 rounded-full hover:bg-surface-sunken transition-colors duration-150"
                                                                title={`Rechercher ${fullName} sur LinkedIn`}
                                                            >
                                                                <LinkedInIcon className="w-4 h-4 text-ink-muted hover:text-ink-muted" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {record?.founders.length > 3 && (
                                            <p className="text-xs text-ink-faint mt-2">
                                                +{record?.founders.length - 3} autre(s) dirigeant(s)
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Partners (if no founders found) */}
                        {(!record?.founders || record?.founders.length === 0) && record?.partners && record?.partners.length > 0 && (
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Users className="w-5 h-5 text-ink-muted" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-ink mb-2">
                                        Dirigeants / Associés ({record?.partners.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {record?.partners.slice(0, 3).map((partner, index) => {
                                            // Try to extract first and last name from partner.name
                                            const fullName = partner.name || "Nom non renseigné";
                                            const nameParts = fullName.split(' ');
                                            const firstName = nameParts[0] || "";
                                            const lastName = nameParts.slice(1).join(' ') || "";
                                            const companyName = record?.company.denomination || "";
                                            
                                            const handleGoogleSearch = (e: React.MouseEvent) => {
                                                e.preventDefault();
                                                if (firstName && lastName && partner.type !== 'moral') {
                                                    const url = generateGoogleSearchUrl(firstName, lastName, companyName);
                                                    window.open(url, '_blank', 'noopener,noreferrer');
                                                }
                                            };
                                            
                                            const handleLinkedInSearch = (e: React.MouseEvent) => {
                                                e.preventDefault();
                                                if (firstName && lastName && partner.type !== 'moral') {
                                                    const url = generateLinkedInSearchUrl(firstName, lastName, companyName);
                                                    window.open(url, '_blank', 'noopener,noreferrer');
                                                }
                                            };
                                            
                                            return (
                                                <div key={index} className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <span className="text-xs font-medium text-green-700">
                                                            {(partner.name || "").charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-ink truncate">
                                                            {partner.name || "Nom non renseigné"}
                                                        </p>
                                                        <p className="text-xs text-ink-faint truncate">
                                                            {partner.role} • {partner.type === 'moral' ? 'Personne morale' : 'Personne physique'}
                                                        </p>
                                                    </div>
                                                    {/* Search Icons - Only show for physical persons (not moral entities) */}
                                                    {firstName && lastName && partner.type !== 'moral' && (
                                                        <div className="flex items-center gap-1 ml-2">
                                                            <button
                                                                onClick={handleGoogleSearch}
                                                                className="p-1 rounded-full hover:bg-surface-sunken transition-colors duration-150"
                                                                title={`Rechercher ${fullName} sur Google`}
                                                            >
                                                                <GoogleIcon className="w-4 h-4 text-ink-muted hover:text-ink" />
                                                            </button>
                                                            <button
                                                                onClick={handleLinkedInSearch}
                                                                className="p-1 rounded-full hover:bg-surface-sunken transition-colors duration-150"
                                                                title={`Rechercher ${fullName} sur LinkedIn`}
                                                            >
                                                                <LinkedInIcon className="w-4 h-4 text-ink-muted hover:text-ink-muted" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {record?.partners.length > 3 && (
                                            <p className="text-xs text-ink-faint mt-2">
                                                +{record?.partners.length - 3} autre(s)
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Establishments */}
                        {record?.establishments && record?.establishments.length > 0 && (
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Building className="w-5 h-5 text-ink-muted" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-ink mb-2">
                                        Établissements ({record?.establishments.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {record?.establishments.slice(0, 2).map((establishment, index) => (
                                            <div key={index} className="p-3 bg-surface-sunken rounded-lg">
                                                <p className="text-sm font-medium text-ink">{establishment.type}</p>
                                                <p className="text-xs text-ink-muted mt-1">{establishment.activity}</p>
                                                <p className="text-xs text-ink-faint mt-1">{formatAddress(establishment.address)}</p>
                                            </div>
                                        ))}
                                        {record?.establishments.length > 2 && (
                                            <p className="text-xs text-ink-faint mt-2">
                                                +{record?.establishments.length - 2} autre(s) établissement(s)
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-surface-sunken">
            {/* Hero Section - Pappers Style */}
            <div className="bg-surface border-b border-border">
                <div className="max-w-6xl mx-auto px-4 py-12">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-ink mb-4">
                            Toute l'information des entreprises
                        </h1>
                        <p className="text-xl text-ink-muted mb-8">
                            Gratuite, intelligente, complète
                        </p>
                        
                        {/* Search Form - Enhanced with search type selection */}
                        <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
                            {/* Search Type Selector */}
                            <div className="flex justify-center mb-4">
                                <div className="bg-surface-sunken p-1 rounded-lg inline-flex">
                                    <button
                                        type="button"
                                        onClick={() => setSearchType("identifier")}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                            searchType === "identifier"
                                                ? "bg-surface text-ink-muted shadow-sm"
                                                : "text-ink-muted hover:text-ink"
                                        }`}
                                    >
                                        SIREN / SIRET
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSearchType("name")}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                            searchType === "name"
                                                ? "bg-surface text-ink-muted shadow-sm"
                                                : "text-ink-muted hover:text-ink"
                                        }`}
                                    >
                                        Recherche par nom
                                    </button>
                                </div>
                            </div>

                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={
                                        searchType === "identifier"
                                            ? "SIREN (9 chiffres) ou SIRET (14 chiffres)"
                                            : "Rechercher une entreprise par nom (ex: Microsoft France)"
                                    }
                                    className="w-full px-6 py-4 text-lg border-2 border-border-strong rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-ink pl-14 pr-20"
                                    maxLength={searchType === "identifier" ? 14 : undefined}
                                />
                                <Search className="w-6 h-6 text-ink-faint absolute left-4 top-1/2 transform -translate-y-1/2" />
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 px-6 py-2 bg-ink text-ink-inverse font-medium rounded-lg hover:bg-ink/90 focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? "..." : "Rechercher"}
                                </button>
                            </div>
                            <p className="text-sm text-ink-faint mt-3">
                                {searchType === "identifier"
                                    ? (() => {
                                        const t = detectedIdentifierType(searchQuery);
                                        if (t === "siret") return "SIRET détecté — recherche sur l'établissement exact";
                                        if (t === "siren") return "SIREN détecté — recherche sur l'entreprise";
                                        return "Saisissez un SIREN (9 chiffres) ou un SIRET (14 chiffres)";
                                    })()
                                    : "Recherchez par nom d'entreprise pour trouver les informations légales et dirigeants"
                                }
                            </p>
                        </form>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2">
                            <Info className="w-5 h-5 text-red-600" />
                            <span className="text-red-800 font-medium">Erreur</span>
                        </div>
                        <p className="text-red-700 mt-1">{error}</p>
                    </div>
                )}

                {/* Results */}
                {searchResult && (
                    <div>
                        <div className="mb-6">
                            <h2 className="text-2xl font-semibold text-ink mb-2">
                                {searchType === "identifier"
                                    ? `Résultats pour le ${searchResult.siret ? "SIRET" : "SIREN"} ${searchResult.siret || searchResult.siren || searchQuery}`
                                    : `Résultats pour "${searchResult.searchTerm || searchQuery}"`
                                }
                            </h2>
                            <p className="text-ink-muted">
                                {searchResult.meta.recordCount} enregistrement(s) trouvé(s) • Recherche effectuée le {formatDate(searchResult.meta.searchedAt)}
                                {searchType === "name" && ` • Type: recherche par nom`}
                            </p>
                        </div>

                        {searchResult.data.length === 0 ? (
                            <div className="bg-surface rounded-lg border border-border p-12 text-center">
                                <Building className="w-16 h-16 text-ink-faint mx-auto mb-6" />
                                <h3 className="text-xl font-medium text-ink mb-3">
                                    Aucune entreprise trouvée
                                </h3>
                                <p className="text-ink-muted mb-6">
                                    {searchType === "identifier"
                                        ? "Aucune information n'a été trouvée pour cet identifiant dans nos bases de données."
                                        : "Aucune entreprise ne correspond à votre recherche."
                                    }
                                </p>
                                <p className="text-sm text-ink-faint">
                                    {searchType === "identifier"
                                        ? "Vérifiez que le SIREN (9 chiffres) ou le SIRET (14 chiffres) est correct."
                                        : "Essayez avec un nom d'entreprise différent ou vérifiez l'orthographe."
                                    }
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {searchResult.data.map((record) => (
                                    <CompanyCard key={record?.id} record={record} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Empty State */}
                {!searchResult && !error && !loading && (
                    <div className="bg-surface rounded-lg border border-border p-12 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-20 h-20 bg-surface-sunken rounded-full flex items-center justify-center mx-auto mb-6">
                                <Building className="w-10 h-10 text-ink-muted" />
                            </div>
                            <h3 className="text-xl font-medium text-ink mb-3">
                                Recherche d'entreprise
                            </h3>
                            <p className="text-ink-muted mb-6">
                                Utilisez notre moteur de recherche pour obtenir toutes les informations légales,
                                juridiques et financières d'une entreprise. Recherchez par SIREN, SIRET ou nom d'entreprise.
                            </p>
                            <div className="grid grid-cols-3 gap-4 text-sm text-ink-faint">
                                <div className="text-center">
                                    <FileText className="w-6 h-6 mx-auto mb-2 text-ink-faint" />
                                    <span>Données BODACC</span>
                                </div>
                                <div className="text-center">
                                    <Users className="w-6 h-6 mx-auto mb-2 text-ink-faint" />
                                    <span>Dirigeants</span>
                                </div>
                                <div className="text-center">
                                    <Banknote className="w-6 h-6 mx-auto mb-2 text-ink-faint" />
                                    <span>Informations légales</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Create Company Modal */}
            {showCreateCompanyModal && selectedCompanyData && (
                <CreateCompanyModal
                    isOpen={showCreateCompanyModal}
                    onClose={() => {
                        setShowCreateCompanyModal(false);
                        setSelectedCompanyData(null);
                    }}
                    companyData={selectedCompanyData}
                />
            )}
        </div>
    );
};

export default ScanCompany;